import { buyMore } from "@/api/add-to-cart";
import { CarrierElementType, ContentType } from "@/api/scode/scode_params";
import { useAddPurchasePanel } from "@/components/add-purchase-panel/hook/useAddPurchasePanel";
import PosterSwipe, { usePosterSwipePro } from "@/components/poster-swiper/pro";
import useCheckBind from "@/pages/new-person/hooks/useCheckBind";
import BindPhonePopup from "@/pages/personal/components/bind-phone-popup";
import PrdActivityPopup from "@/pages/promote/components/prd-activity-popup";
import useShare from "@/pages/promote/share";
import { useCommonContext } from "@/utils/context/common";
import { usePageTitle } from "@/utils/page_utils";
import useNavigation from "@/utils/useNavigate";
import { ProductModel } from "@akc/biz-api";
import { VideoCardMaterialSwiperComp } from "@akc/mx-taro-h5-components";
import { useMount, useUnmount } from "ahooks";
import { Mask, Toast } from "antd-mobile";
import Cookie from "js-cookie";
import React, { useRef, useState } from "react";
import { useActivate, useUnactivate } from "react-activation";
import { useSearchParams } from "react-router-dom";
import TopNavBar from "./components/TopNavBar";
import styles from "./index.module.less";

/**
 * 素材视频大卡页
 * @param materialNo 素材编号
 * @param activitySpuId 活动商品id
 * @param spuId 基础商品spuId
 * @param distributorId 分销员id
 * @param shopId 店铺id
 * @returns
 */
const MaterialVideoCardList: React.FC = () => {
  const { userVisitInfo, track, cartNum } = useCommonContext();
  const userIdRef = useRef<string>(Cookie.get("msUserCode") || "userId");
  const [searchParams] = useSearchParams();
  // const activitySpuId = searchParams.get("activitySpuId");
  const materialNo = searchParams.get("materialNo");
  const activitySpuId = searchParams.get("activitySpuId");
  const spuId = searchParams.get("spuId");

  const [currentProduct, setCurrentProduct] = useState<ProductModel | null>(null);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [searchRequestId, setSearchRequestId] = useState<string>("");
  const [showPrdActivityPopup, setShowPrdActivityPopup] = useState(false);
  const [showGuide, setShowGuide] = useState(true);
  const [isBackground, setIsBackground] = useState(false);
  // 是否展示系统分享引导气泡
  const [showShareMask, setShowShareMask] = useState<boolean>(false);
  // const [notifyFollowMaterial, setChangingFollowProduct] = useState<{
  //   isFollow?: boolean;
  //   spuId?: string;
  // } | null>(null);
  // 最后一个分享信息请求的id
  const lastShareRequestIdRef = useRef<number>(0);

  const {
    bindPhonePopupVisible,
    setBindPhonePopupVisible,
    createBindPhoneTask,
    completeBindPhoneTask,
    toAuth,
  } = useCheckBind();

  const { openPanel, closePanel, AddPurchasePanelComponent } = useAddPurchasePanel();

  // 素材转发海报控制器
  const posterControl = usePosterSwipePro();

  const { navigate2Product, navigate2Activity, navigate2Store, navigate2Cart } = useNavigation();

  usePageTitle("素材视频");

  const useShareProps = useShare();

  const openMoreProductDialog = () => {
    setShowPrdActivityPopup(true);
  };

  const closeMorePrdActivityPopup = () => {
    setShowPrdActivityPopup(false);
  };

  const handleTopNavBarSearch = () => {
    if (track && track.track) {
      track.track("resource_click", {
        page_name: "素材视频大卡",
        btn_name: "搜索",
        resource_type: "视频",
        resource_rank: currentIndex + 1,
        domain: "素材视频大卡",
        activity_id: currentProduct?.activityId,
        brand_id: currentProduct?.brandInfo?.brandId,
        activity_spu_code: currentProduct?.activitySpuId,
        sku_id: currentProduct?.selectSkuId,
        material_id: currentProduct?.materialRelationProductInfoVO?.materialNo,
        search_request_id: searchRequestId,
      });
    }
  };

  // 点击商品，进商详
  const handleProductClick = (product: ProductModel) => {
    if (!product.activitySpuId) {
      return;
    }
    navigate2Product({ activitySpuId: product.activitySpuId, forceNormalProduct: true });
  };

  // 点击档期
  const handleActivityClick = (material: ProductModel) => {
    if (!material.activityId) {
      return;
    }
    navigate2Activity({ activityNo: material.activityId });
  };

  //  点击视频底部操作中的「店铺/品牌」按钮
  const handleShopClick = (material: ProductModel) => {
    if (!material.activitySimpleVO?.merchantShopCode) {
      return;
    }
    navigate2Store({
      merchantShopCode: material.activitySimpleVO?.merchantShopCode,
      shopCode: material.activitySimpleVO?.merchantShopCode,
      page_name: encodeURIComponent("素材视频大卡"),
      productType: 1,
    });
  };

  // 点击立即购买，拉起商品规格弹窗
  const handleBuyAction = async (product: ProductModel) => {
    if (!userVisitInfo?.isAuthorized) {
      toAuth();
      return;
    }
    await createBindPhoneTask();
    openPanel({
      product,
      buttonType: 3,
    });
  };

  // 加购
  const handleAddCartAction = async (product: ProductModel) => {
    if (!userVisitInfo?.isAuthorized) {
      toAuth();
      return;
    }

    await createBindPhoneTask();
    openPanel({
      product,
      buttonType: 2,
    });
  };

  // 还想买
  const handleWant2BuyAction = async (product: ProductModel) => {
    if (!userVisitInfo?.isAuthorized) {
      toAuth();
      return;
    }
    await createBindPhoneTask();
    try {
      const response = await buyMore({
        activityId: product.activityId,
        activitySpuId: product.activitySpuId,
        selectSkuId: product.selectSkuId,
      });
      if (response) {
        Toast.show("你的购买意愿已通知商家");
      }
    } catch (error) {
      console.error(error);
    }
  };

  // 点击转发素材
  const onForwardMaterialAction = (material: ProductModel) => {
    const materialNo = material?.materialRelationProductInfoVO?.materialNo;
    if (!materialNo) {
      console.error("素材编号materialNo不存在");
      return;
    }
    setShowShareMask(true);
    if (track && track.track) {
      track.track("$share", {
        domain: "素材视频大卡",
        material_id: material?.materialRelationProductInfoVO?.materialNo,
        page_name: "素材视频大卡",
        share_channel: "微信好友/群",
        share_name: "转发卡片",
        share_num: "1",
        shaer_setting: "H5",
        is_default: false,
        share_t: new Date().valueOf(),
        share_type: "素材",
      });
    }
  };

  // 素材点赞
  const onCollectPrdAction = (material: ProductModel, newFollowInfo: { newIsFollow: boolean }) => {
    const { materialNo } = material?.materialRelationProductInfoVO || {};
    if (!materialNo) {
      console.error("素材编号materialNo不存在");
      return;
    }
    const { newIsFollow } = newFollowInfo || {};
    sessionStorage.setItem(
      "MATERIAL_VIDEO_CARD_FELLOW_CHANGE",
      JSON.stringify({
        materialNo,
        newFollowInfo: {
          newIsFollow,
          // newFollowNum ，视频大卡接口没有返回 followNum,所以newFollowNum的值是错的，不能用
        },
      }),
    );
  };

  // 关联商品弹窗的商品转发事件
  const onForwardPrdActionForPopup = (product: ProductModel) => {
    if (!product) {
      return;
    }

    if (!product.activitySpuId) {
      return;
    }
    posterControl.open({
      bizInfo: {
        contentId: product.activitySpuId,
        contentType: ContentType.spuProduct,
        contentDetailId: product.selectSkuId,
      },
      envContext: {
        sourceScene: "PRODUCT_VIDEO_CARD",
      },
      uiConfig: {
        btnConfig: ["copy", "edit"],
      },
    });
  };

  // 素材视频大卡列表上下滑动切换
  const handleIndexChange = (material: ProductModel, index: number, requestId: string) => {
    if (!material) {
      return;
    }
    // if (index !== 0) {
    //   setShowGuide(false);
    // }
    setCurrentProduct(material);
    setCurrentIndex(index);
    if (requestId) {
      setSearchRequestId(requestId);
    }
    // 设置系统分享信息
    setSystemShareInfoConfig(material);
  };

  // 设置系统分享信息
  const setSystemShareInfoConfig = async (material: ProductModel) => {
    const { activitySpuId, spuId } = material || {};
    const { materialNo } = material?.materialRelationProductInfoVO || {};
    /**
     * 短链解析逻辑：
     * 1、shareCardInfo接口返回一个短链，如"https://h5shop-stable.akucun.com/api/shortCode/PTyq5A"。
     * 同时，会员端会将"share.material.pageName"作为短链映射的页面地址上报给会员服务，如果没传share.material.pageName则会根据contentExtra.share_grass_growing_location上报一个默认地址
     * 2、访问短链后，短链服务会自动解析成长链，地址就是域名+会员上报的页面地址+会员上报的参数信息
     */
    const contentExtra = {
      "share.material.pageName": "/mx-shop-micro/material-video-list",
      spuId,
      activitySpuId,
    };
    useShareProps.shareSetting(3, materialNo, null, contentExtra);
  };

  useMount(() => {
    setIsBackground(false);
    // 触发一次pageview埋点
    if (track && track.track) {
      track.track("$pageview", {
        page_name: "素材视频大卡",
        previous_page_name: searchParams.get("previous_page_name"),
        domain: "素材视频大卡",
        material_id: materialNo,
      });
    }
  });

  useUnmount(() => {
    setIsBackground(true);
  });

  useActivate(() => {
    setIsBackground(false);
    // try {
    //   const info = localStorage.getItem("notifyFollowMaterial");
    //   if (info) {
    //     console.log("changingFollowProduct商品", info);
    //     setChangingFollowProduct(JSON.parse(info));
    //     localStorage.removeItem("notifyFollowMaterial");
    //   }
    // } catch (error) {
    //   console.error("获取changingFollowProduct失败:", error);
    // }
  });

  useUnactivate(() => {
    setIsBackground(true);
    // setChangingFollowProduct(null);
  });

  return (
    <div>
      {/* 自定义导航栏 */}
      <TopNavBar onSearchClick={handleTopNavBarSearch} />

      {/* 素材视频大卡swiper */}
      <VideoCardMaterialSwiperComp
        activitySpuId={activitySpuId}
        materialNo={materialNo}
        baseProductIds={spuId}
        titleFrontLocationCode="H5_ProductList_styleNo1_titleFront"
        titleBottomLocationCode="H5_ProductList_styleNo1_titleDown"
        onProductAction={handleProductClick}
        onMoreProductDialog={openMoreProductDialog}
        onB2RShopAction={handleShopClick}
        onActivityAction={handleActivityClick}
        onBuyAction={handleBuyAction}
        onAddCartAction={handleAddCartAction}
        onForwardAction={onForwardMaterialAction}
        onCollectAction={onCollectPrdAction}
        onWant2BuyAction={handleWant2BuyAction}
        onIndexChange={handleIndexChange}
        // notifyFollowMaterial={notifyFollowMaterial}
        isBackground={isBackground}
        // showGuide={showGuide}
        trackData={{
          page_name: "素材视频大卡",
          domain: "素材视频大卡",
        }}
        userId={userIdRef.current}
        theme="dark"
        nextMarginLimit={0}
      />

      {/* 加购弹窗组件 */}
      {AddPurchasePanelComponent}

      {/* 转发海报 */}
      <PosterSwipe {...posterControl.props} />

      {/* 关联商品、档期、会场弹窗 */}
      {showPrdActivityPopup && (
        <PrdActivityPopup
          queryBy="MATERIAL"
          activitySpuId={currentProduct?.activitySpuId}
          materialNo={currentProduct?.materialRelationProductInfoVO?.materialNo}
          trackDataCommon={{
            page_name: "素材视频大卡",
            domain: "素材视频大卡",
          }}
          onClose={closeMorePrdActivityPopup}
          onProductClick={handleProductClick}
          onBuyAction={handleBuyAction}
          onAddCartAction={handleAddCartAction}
          onForwardaPrdAction={onForwardPrdActionForPopup}
        />
      )}

      {/* 绑定手机号弹窗 */}
      <BindPhonePopup
        visible={bindPhonePopupVisible}
        setVisible={setBindPhonePopupVisible}
        onFinish={() => {
          completeBindPhoneTask();
        }}
      />

      {/* 系统分享引导气泡 */}
      <Mask
        visible={showShareMask}
        getContainer={document.getElementById("root")}
        className={styles["share-mask-container"]}
        onMaskClick={() => setShowShareMask(false)}>
        <img
          title="分享引导气泡"
          className={styles["share-guide"]}
          src="https://akim-oss.aikucun.com/342fd5035fac892693a1b04f6a73c3a957a0617d_1700105346385_60.png"
        />
      </Mask>
    </div>
  );
};

export default MaterialVideoCardList;
